import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ListPlus, Trash2, Building2, DoorClosed } from "lucide-react";

export default function ChecklistManager() {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState("all");
  const [textEn, setTextEn] = useState("");
  const [textHi, setTextHi] = useState("");
  const [textMr, setTextMr] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/properties").then((r) => {
      setProperties(r.data.properties);
      if (r.data.properties.length) setPropertyId(r.data.properties[0].id);
    });
  }, []);

  useEffect(() => {
    if (!propertyId) return;
    setRoom("all");
    Promise.all([
      api.get("/config/rooms", { params: { property_id: propertyId } }),
      api.get("/custom-items", { params: { property_id: propertyId } }),
    ]).then(([a, b]) => {
      setRooms(a.data.rooms);
      setItems(b.data);
    });
  }, [propertyId]);

  const floorGroups = useMemo(() => {
    const g = {};
    rooms.forEach((r) => {
      if (!g[r.floor]) g[r.floor] = [];
      g[r.floor].push(r);
    });
    return Object.entries(g).sort(([a], [b]) => Number(a) - Number(b));
  }, [rooms]);

  const onAdd = async () => {
    if (!textEn.trim()) {
      toast.error("Checklist point text (English) is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/custom-items", {
        property_id: propertyId,
        room_number: room === "all" ? null : room,
        text_en: textEn,
        text_hi: textHi || null,
        text_mr: textMr || null,
      });
      toast.success("Checklist point added — it is now compulsory for supervisors");
      setTextEn(""); setTextHi(""); setTextMr("");
      const { data } = await api.get("/custom-items", { params: { property_id: propertyId } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (item) => {
    try {
      await api.delete(`/custom-items/${item.id}`);
      toast.success("Checklist point removed");
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <Layout title="Checklist Points" subtitle="Management · Add Room-wise & Property-wise Points">
      <div className="bg-white border border-[var(--border-warm)] rounded-lg p-6 mb-6">
        <div className="hotel-overline mb-3">Property</div>
        <div className="flex flex-wrap gap-2">
          {properties.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`cm-property-${p.id}`}
              onClick={() => setPropertyId(p.id)}
              className={`sop-pill px-4 h-11 rounded-md border-2 text-sm font-semibold flex items-center gap-2 ${
                propertyId === p.id
                  ? "bg-[var(--navy)] border-[var(--navy)] text-white"
                  : "bg-white border-[var(--border-warm)] text-[var(--ink-2)] hover:border-[var(--navy)]"
              }`}
            >
              <Building2 className="w-4 h-4" /> {p.name.replace(" Hotel", "")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Add form */}
        <div className="bg-white border border-[var(--border-warm)] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-md bg-[var(--gold-light)] text-[var(--gold)] flex items-center justify-center border border-[var(--gold)]/40">
              <ListPlus className="w-5 h-5" />
            </div>
            <div>
              <div className="hotel-overline">New point</div>
              <h3 className="font-display text-2xl text-[var(--navy)]">Add Checklist Point</h3>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Applies to</Label>
              <Select value={room} onValueChange={setRoom}>
                <SelectTrigger data-testid="cm-room-select" className="mt-1.5 h-11 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all" data-testid="cm-room-option-all">Entire property (all rooms)</SelectItem>
                  {floorGroups.map(([floor, list]) => (
                    <div key={floor}>
                      <div className="px-2 py-1 hotel-overline">Floor {floor}</div>
                      {list.map((r) => (
                        <SelectItem key={r.number} value={r.number} data-testid={`cm-room-option-${r.number}`}>
                          Room {r.number} · {r.type}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">Point text (English) *</Label>
              <Input data-testid="cm-text-en" value={textEn} onChange={(e) => setTextEn(e.target.value)} placeholder="e.g. Balcony furniture wiped and aligned" className="mt-1.5 h-11 bg-white border-[var(--border-warm)]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">हिंदी (optional)</Label>
              <Input data-testid="cm-text-hi" value={textHi} onChange={(e) => setTextHi(e.target.value)} placeholder="हिंदी में लिखें" className="mt-1.5 h-11 bg-white border-[var(--border-warm)]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-hotel text-[var(--ink-2)]">मराठी (optional)</Label>
              <Input data-testid="cm-text-mr" value={textMr} onChange={(e) => setTextMr(e.target.value)} placeholder="मराठीत लिहा" className="mt-1.5 h-11 bg-white border-[var(--border-warm)]" />
            </div>
            <Button data-testid="cm-add-button" onClick={onAdd} disabled={busy} className="w-full h-11 bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold">
              {busy ? "Adding…" : (<><ListPlus className="w-4 h-4 mr-2" /> Add Point</>)}
            </Button>
          </div>
        </div>

        {/* Existing points */}
        <div className="bg-white border border-[var(--border-warm)] rounded-lg p-6">
          <div className="hotel-overline mb-1">Active points</div>
          <h3 className="font-display text-2xl text-[var(--navy)] mb-4">Additional Points — {properties.find((p) => p.id === propertyId)?.name.replace(" Hotel", "") || ""}</h3>
          {items.length === 0 ? (
            <div className="text-sm text-[var(--muted)] py-8 text-center border border-dashed border-[var(--border-warm)] rounded-md">
              No additional points yet. Points you add become compulsory on the supervisor checklist.
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => (
                <li key={it.id} data-testid={`cm-item-row-${it.id}`} className="flex items-start gap-3 border border-[var(--border-subtle)] rounded-md p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0 ${
                    it.room_number ? "bg-[#DBEAFE] text-[#1D4ED8]" : "bg-[var(--gold-light)] text-[var(--gold)]"
                  }`}>
                    {it.room_number ? (<><DoorClosed className="w-3 h-3" /> Room {it.room_number}</>) : (<><Building2 className="w-3 h-3" /> Property-wide</>)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--ink)]">{it.text_en}</div>
                    {(it.text_hi || it.text_mr) && (
                      <div className="text-xs text-[var(--muted)] mt-0.5">{[it.text_hi, it.text_mr].filter(Boolean).join(" · ")}</div>
                    )}
                  </div>
                  <Button variant="outline" size="sm" data-testid={`cm-delete-${it.id}`} onClick={() => onDelete(it)} className="border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger-bg)] shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  );
}
